/**
 * AppErrorBoundary -- Top-level React error boundary.
 *
 * Catches uncaught render errors anywhere in the component tree and
 * displays a recovery UI. The operator can either reload the page
 * or navigate back to the dashboard without a full page reload.
 *
 * Placed as the outermost wrapper in AppRouter so that even router
 * errors (e.g., lazy-load failures) are caught.
 *
 * @module router/AppErrorBoundary
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly hasError: boolean;
  readonly errorMessage: string;
  readonly componentStack: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
      componentStack: '',
    };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const message =
      error instanceof Error
        ? error.message
        : 'An unexpected error occurred.';

    return {
      hasError: true,
      errorMessage: message,
    };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Preserve component stack for diagnostics; strip in production builds
    // if a privacy policy requires it.
    this.setState({
      componentStack: info.componentStack ?? '',
    });

    // Surface to console so DevTools / Sentry picks it up.
    console.error('[AppErrorBoundary] Unhandled render error:', error, info);
  }

  private handleGoToDashboard = (): void => {
    // Use location.href to do a full navigation to /dashboard, which will
    // unmount the errored subtree and give React a clean slate.
    window.location.href = '/dashboard';
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        className="min-h-screen bg-slate-950 flex items-center justify-center p-6"
        role="alert"
        aria-live="assertive"
      >
        <div className="w-full max-w-md text-center space-y-6">
          {/* Icon */}
          <div
            className="mx-auto w-16 h-16 rounded-full bg-red-900/40 border border-red-700/50
                       flex items-center justify-center"
            aria-hidden="true"
          >
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>

          {/* Heading */}
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-slate-100">
              Something went wrong
            </h1>
            <p className="text-sm text-slate-400">
              An unexpected error occurred. Your solver jobs are unaffected.
            </p>
          </div>

          {/* Error detail (collapsed) */}
          {this.state.errorMessage && (
            <div
              className="text-left rounded-lg border border-slate-700 bg-slate-900 px-4 py-3
                         font-mono text-xs text-red-300 break-all"
              aria-label="Error details"
            >
              {this.state.errorMessage}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleGoToDashboard}
              className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg
                         bg-blue-600 hover:bg-blue-500 active:bg-blue-700
                         text-white text-sm font-medium
                         focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2
                         focus:ring-offset-slate-950 transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg
                         bg-slate-700 hover:bg-slate-600 active:bg-slate-800
                         text-slate-200 text-sm font-medium
                         border border-slate-600
                         focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2
                         focus:ring-offset-slate-950 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
